import React, { useCallback, useContext, useEffect, useState } from 'react';
import ConnectedFormContext from '../../../components/Context/ConnectedFormContext';
import { useHistory } from 'react-router';
import ModalContext from '../../../components/Context/ModalContext';
import { IJobMeta, useBackgroundMutation } from '../../../utils/BackgroundJobs';
import SalesOrderDelayedPostSaveMutation from '../../../graphql/mutations/sales/SalesOrderDelayedPostSaveMutation.graphql';
import { alternateFullScreenModalStyle } from '../../../components/modals/Styles';
import PurchaseRequirementDetailPreviewWorkspace from '../../purchases/PurchaseRequirementDetailPreviewWorkspace';
import {
  ActionType,
  AlertType,
  FilterOperation,
  IFilterInput,
  IMetaAction,
  ISalesOrderDelayedPostSaveMutationResult,
  ISalesOrderDelayedPostSaveMutationVariables,
  ISalesOrderInput,
  Maybe,
  ReportType,
  SalesOrderActivationType,
} from '../../../graphql/schema/p4types';
import { serviceActions } from '../../../state/modules/service';
import { useDispatch, useSelector } from 'react-redux';
import { IExtendedMetaAction } from './useSalesOrderPostSave';
import FluentModal from '../../../components/modals/FluentModal';
import { IReduxStore } from '../../../@types/reduxTypes';
import { useP4DocumentController } from '../../../components/document/DocumentProvider';

const SHORTAGE_MODAL_STYLES = { main: { minWidth: '1000px' } };

/**
 * Return a function that takes an IMetaAction Array and either triggers document carousel printing (If enabled)
 * or an Array of legacy actions if it's disabled or not yet supported for a given action type.
 */
const useProcessMetaActionArray = () => {
  const enableDocumentCarousel = useSelector((store: IReduxStore) => {
    return store.app.fullUserDetails?.flags?.documentCarouselEnabled ?? false;
  });
  const { printDocumentSingle } = useP4DocumentController();
  const { values } = useContext(ConnectedFormContext);

  return (metadata: Array<IMetaAction>): Array<IExtendedMetaAction> => {
    const legacyActions: Array<IExtendedMetaAction> = [];
    for (const entry of metadata) {
      if (enableDocumentCarousel) {
        // If document carousel feature enabled, dispatch print actions to the DocumentProvider rather than handling them here.
        // We don't have a result here, so have no choice but to fall back on values.id (Which the legacy action would have needed to do too).
        switch (entry.actionType) {
          case ActionType.PrintAcknowledgement:
            printDocumentSingle(ReportType.SalesAcknowledgement, (values as ISalesOrderInput)?.id);
            break;
          case ActionType.PrintPickingInstruction:
            printDocumentSingle(ReportType.SalesPickingList, (values as ISalesOrderInput)?.id);
            break;
          default:
            const action: IExtendedMetaAction = {
              __typename: 'MetaAction',
              actionType: entry.actionType,
              jobInfo: 'jobInfo' in entry ? entry.jobInfo : null,
              postSave: true,
            };
            legacyActions.push(action);
        }
      } else {
        const action: IExtendedMetaAction = {
          __typename: 'MetaAction',
          actionType: entry.actionType,
          jobInfo: 'jobInfo' in entry ? entry.jobInfo : null,
          postSave: true,
        };
        legacyActions.push(action);
      }
    }
    return legacyActions;
  };
};

const ShortageModal = ({
  modalOpen,
  metaAction,
  addActions,
  doDelayedPostSave = true,
}: {
  modalOpen: boolean;
  metaAction?: any;
  addActions?: (actions: IExtendedMetaAction[]) => void;
  doDelayedPostSave?: boolean;
}) => {
  const connectedForm = useContext(ConnectedFormContext);
  const { values: unTypedValues, match } = connectedForm;
  const dispatch = useDispatch();
  const values: Maybe<ISalesOrderInput> = unTypedValues;
  const history = useHistory();
  const { onClose } = useContext(ModalContext);
  const [isOpen, setIsOpen] = useState(modalOpen);

  useEffect(() => {
    //'modalOpen' prop is source-of-truth, however we want to "minimize" the modal while the background job runs before we call props.close, to ensure data is all in-sync as expected.
    setIsOpen(modalOpen);
  }, [modalOpen]);

  const jobFilter: Array<IFilterInput> = [
    {
      filterOperation: FilterOperation.Equals,
      filterParameters: {
        field: 'jobId',
        value: metaAction?.jobInfo?.jobId,
      },
    },
  ];

  //checks whether the given object is of type IMetaAction
  const isIMetaAction = (object: any): object is IMetaAction => {
    return 'actionType' in object;
  };

  //Checks whether the given object is an array of IMetaAction
  const isIMetaActionArr = (object: any): object is IMetaAction[] => {
    const isArray = Array.isArray(object);
    if (isArray && object.length > 0) {
      //check that all items are of type IMetaAction
      return object.every(isIMetaAction);
    }
    return true;
  };

  const processMetaActionArray = useProcessMetaActionArray();

  //extracts the meta actions from a JobInfo metadata
  const extractMetaActions = (jobInfo: IJobMeta): void => {
    //process any new actions
    const jobInfoProperties = jobInfo.properties.metadata;
    if (addActions && jobInfoProperties) {
      try {
        const metadata = JSON.parse(jobInfoProperties);
        //extract the actions
        if (isIMetaActionArr(metadata)) {
          const legacyActions: IExtendedMetaAction[] = processMetaActionArray(metadata);
          if (legacyActions.length > 0) {
            addActions(legacyActions);
          }
        }
      } catch (exception) {
        console.error(
          'Problem parsing metadata, could not parse the supplied metadata on the job meaning there could be a ' +
            'problem showing the post order modals e.g. picking list'
        );
        dispatch(
          serviceActions.genericAlert(
            'Problem processing post sales events',
            'Some post sales events may not display correctly.',
            AlertType.Warning
          )
        );
      }
    }
  };

  const [salesOrderDelayedPostSave] = useBackgroundMutation<
    ISalesOrderDelayedPostSaveMutationResult,
    ISalesOrderDelayedPostSaveMutationVariables
  >(SalesOrderDelayedPostSaveMutation, {
    onCompleteJob: (jobInfo: IJobMeta) => {
      extractMetaActions(jobInfo);
      //Call modal close to trigger the next modal or navigation as appropriate.
      onClose?.();
      //Trigger a concurrency reload to update concurrencyVersion and any other relevant fields, so if the user doesn't print any documents (Which would also call concurrencyReloadData),
      //then the form is still brought up to date for further editing.
      connectedForm.concurrencyReloadData?.();
    },
  });

  /**
   * Close the shortage modal & trigger delayed post-save logic, including tagging the lines as having had shortages processed.
   */
  const onCloseModal = useCallback(() => {
    const variables: ISalesOrderDelayedPostSaveMutationVariables = {
      input: {
        clientMutationId: Math.random().toString(36).substr(2, 32),
        salesOrderId: values?.id,
      },
    };

    //Delayed post save will trigger automatic allocations if configured to do so.
    if (doDelayedPostSave) {
      salesOrderDelayedPostSave({ variables: variables });
    } else {
      onClose?.();
    }
    setIsOpen(false);
  }, [salesOrderDelayedPostSave, values]);

  return (
    <ModalContext.Provider value={{ onClose: onCloseModal, style: alternateFullScreenModalStyle }}>
      <FluentModal isOpen={isOpen} styles={SHORTAGE_MODAL_STYLES}>
        <PurchaseRequirementDetailPreviewWorkspace
          embeddedView={true}
          includeHeader={true}
          disableViewController={true}
          viewScreenName="/Purchasing/Shortages/ShortageDetailPreview/Modal"
          removeAddButton={false}
          predefinedFilters={jobFilter}
          salesOrderNumber={values?.orderNumber}
          scheduledSalesOrder={values?.salesOrderActivationType === SalesOrderActivationType.Scheduled}
          customerName={values?.customer?.name}
          history={history}
          match={match}
          assemblyOptions={false}
        />
      </FluentModal>
    </ModalContext.Provider>
  );
};

export default ShortageModal;
